import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import keycloak from "./keycloak";
import { Spinner } from "../components/spinner";

interface AuthUser {
  username: string;
  email?: string;
}

interface AuthContextValue {
  authenticated: boolean;
  user: AuthUser | null;
  token: string | undefined;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const MIN_LOADING_MS = 500;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"loading" | "authenticated" | "error">("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [minDelayPassed, setMinDelayPassed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMinDelayPassed(true), MIN_LOADING_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    keycloak
      .init({
        onLoad: "login-required",
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      })
      .then((authenticated) => {
        if (authenticated) {
          const parsed = keycloak.tokenParsed;
          setUser({
            username: parsed?.preferred_username ?? parsed?.name ?? "User",
            email: parsed?.email,
          });
          setState("authenticated");
        } else {
          keycloak.login();
        }
      })
      .catch(() => {
        setState("error");
      });
  }, []);

  if (state === "loading" || (state === "authenticated" && !minDelayPassed)) {
    return <Spinner />;
  }

  if (state === "error") {
    return <Spinner />;
  }

  return (
    <AuthContext.Provider
      value={{
        authenticated: true,
        user,
        token: keycloak.token,
        logout: () => keycloak.logout({ redirectUri: window.location.origin }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
